import { axiosInstance } from "../axios.config";

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface LoginResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    userType: string;
    emailVerified: boolean;
    activeOrganizationId: string | null;
    role?: string | null;
  };
  isAuthenticated: boolean;
}

export interface LogoutResponse {
  message: string;
  isAuthenticated: boolean;
}

export interface VerifyEmailPayload {
  token: string;
}

export interface RequestPasswordResetPayload {
  email: string;
}

export interface ValidateResetTokenPayload {
  token: string;
}

export interface ResetPasswordPayload {
  token: string;
  newPassword: string;
}

export interface RegisterCredentials {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  role: "buyer" | "seller" | "both";
  invitationToken?: string;
}

export interface RegisterResponse {
  user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    userType: string;
    emailVerified: boolean;
  };
  message: string;
}

export const authService = {
  /**
   * Login user via marketplace backend
   * Backend handles server-to-server auth and sets httpOnly cookies
   */
  login: async (credentials: LoginCredentials): Promise<LoginResponse> => {
    const response = await axiosInstance.post<LoginResponse>(
      "/auth/login",
      credentials
    );
    return response.data;
  },

  /**
   * Logout user by clearing cookies
   */
  logout: async (): Promise<LogoutResponse> => {
    const response = await axiosInstance.post<LogoutResponse>("/auth/logout");
    return response.data;
  },

  /**
   * Verify email with token
   */
  verifyEmail: async (
    payload: VerifyEmailPayload
  ): Promise<{ message: string }> => {
    const response = await axiosInstance.post<{ message: string }>(
      "/auth/verify-email",
      payload
    );
    return response.data;
  },

  /**
   * Resend verification email
   */
  resendVerificationEmail: async (): Promise<{ message: string }> => {
    const response = await axiosInstance.post<{ message: string }>(
      "/auth/resend-verification"
    );
    return response.data;
  },

  /**
   * Request password reset email
   */
  requestPasswordReset: async (
    payload: RequestPasswordResetPayload
  ): Promise<{ message: string }> => {
    const response = await axiosInstance.post<{ message: string }>(
      "/auth/password-reset/request",
      payload
    );
    return response.data;
  },

  /**
   * Validate password reset token
   */
  validateResetToken: async (
    payload: ValidateResetTokenPayload
  ): Promise<{ valid: boolean; message?: string }> => {
    const response = await axiosInstance.post<{
      valid: boolean;
      message?: string;
    }>("/auth/password-reset/validate", payload);
    return response.data;
  },

  /**
   * Reset password with token
   */
  resetPassword: async (
    payload: ResetPasswordPayload
  ): Promise<{ message: string }> => {
    const response = await axiosInstance.post<{ message: string }>(
      "/auth/password-reset/confirm",
      payload
    );
    return response.data;
  },

  /**
   * Register a new user
   */
  register: async (credentials: RegisterCredentials): Promise<RegisterResponse> => {
    const response = await axiosInstance.post<RegisterResponse>(
      "/auth/register",
      credentials
    );
    return response.data;
  },
};
